import React, { useState } from "react";
import { Container, Row, Col, Card, Form, Button, Alert } from "react-bootstrap";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });

  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertVariant, setAlertVariant] = useState("success");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!formData.name || !formData.email || !formData.message) {
      setAlertMessage("Please fill in all required fields.");
      setAlertVariant("danger");
      setShowAlert(true);
      return;
    }

    setIsSubmitting(true);

    try {
      await fetch(
        "https://script.google.com/macros/s/AKfycbyHi-T11AyhVspPKvOFtjre-ytsqRZng2iSmq1PPDF12HXnREwS_dULCtAFoT3m2SsZ/exec",
        {
          method: "POST",
          mode: "no-cors",                 // prevents CORS issues
          body: JSON.stringify(formData)   // send JSON to Google Script
        }
      );

      // Assume success in no-cors mode
      setAlertMessage("Thank you for contacting us! We will get back to you soon.");
      setAlertVariant("success");
      setShowAlert(true);

      // Reset form
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: ""
      });

    } catch (error) {
      console.error("Error sending message:", error);

      setAlertMessage("Failed to send message. Please try again.");
      setAlertVariant("danger");
      setShowAlert(true);
    } finally {
      setIsSubmitting(false);

      // auto-hide alert
      setTimeout(() => setShowAlert(false), 5000);
    }
  };

  return (
    <Container className="page-shell contact-page my-5">
      <Row className="mb-5 page-header text-center">
        <Col>
          <span className="eyebrow dark">Contact</span>
          <h1 className="mb-3 fw-bold">Contact Us</h1>
          <p className="page-subtitle mx-auto">
            Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
        </Col>
      </Row>

      <Row className="g-4 contact-layout">
        <Col xs={12} md={4}>
          <Card className="h-100 border-0 contact-info-card">
            <Card.Body className="p-4 p-lg-5">
              <h3 className="mb-4">Get in Touch</h3>

              <div className="contact-detail-item">
                <div className="d-flex align-items-center mb-2">
                  <span className="contact-icon">📧</span>
                  <strong>Email</strong>
                </div>
                <p className="mb-0">
                  <a href="mailto:info@sparklogic.org" className="text-decoration-none">
                    info@sparklogic.org
                  </a>
                </p>
              </div>

              <div className="contact-detail-item">
                <div className="d-flex align-items-center mb-2">
                  <span className="contact-icon">📞</span>
                  <strong>Contact Number</strong>
                </div>
                <p className="mb-0">
                  <a href="tel:+919900527300" className="text-decoration-none">
                    +91 99005 27300
                  </a>
                </p>
              </div>

              <div className="contact-detail-item">
                <div className="d-flex align-items-center mb-2">
                  <span className="contact-icon">💬</span>
                  <strong>WhatsApp</strong>
                </div>
                <p className="mb-0">
                  <a
                    href="https://wa.me/919900527300?text=Hi%20SparkLogic,%20I%20would%20like%20to%20know%20more%20about%20your%20courses."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-decoration-none"
                  >
                    +91 99005 27300
                  </a>
                </p>
              </div>

              <hr className="contact-divider" />

              <div className="contact-hours">
                <h5 className="mb-3">Business Hours</h5>
                <p className="mb-1"><strong>Monday - Friday:</strong> 9:00 AM - 6:00 PM</p>
                <p className="mb-1"><strong>Saturday:</strong> 10:00 AM - 4:00 PM</p>
                <p className="mb-0"><strong>Sunday:</strong> Closed</p>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} md={8}>
          <Card className="form-card border-0 contact-form-card">
            <Card.Body className="p-4 p-md-5">
              <h3 className="mb-4">Send us a Message</h3>

              {showAlert && (
                <Alert
                  variant={alertVariant}
                  dismissible
                  onClose={() => setShowAlert(false)}
                >
                  {alertMessage}
                </Alert>
              )}

              <Form onSubmit={handleSubmit} className="contact-form">
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Name <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Email <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Phone Number</Form.Label>
                      <Form.Control
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Subject</Form.Label>
                      <Form.Control
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-4">
                  <Form.Label>
                    Message <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>

                <div className="d-grid contact-submit-wrap">
                  <Button variant="primary" type="submit" size="lg" disabled={isSubmitting}>
                    {isSubmitting ? "Sending..." : "Send Message"}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Contact;
